// Item-related filters

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const itemFilters: FilterRegistry = {
	itemSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name, SKU, manufacturer, category...',
			objectTable: 'item',
			listKeys: [
				'item',
				'item-warehouse-embedded',
				'price_contract_item',
				'clientItemHistory',
			],
			supportsRelevance: true, // Uses OpenSearch for relevance
		}),
		queryParamName: 'itemSearch',
	},

	itemKeywords: {
		...createKeywordFilter({
			label: 'Item Search',
			placeholder: 'Search items...',
			objectTable: 'item',
			listKeys: ['item'],
			supportsRelevance: true, // Uses OpenSearch for relevance
		}),
		queryParamName: 'itemKeywords',
	},

	item_search: {
		...createKeywordFilter({
			label: 'Search Items',
			placeholder: 'Search by Item Name, ERP #, Brand, Manufacturer, UPC, etc',
			objectTable: 'item',
			listKeys: ['item', 'client_shop', 'quote_shop'],
			supportsRelevance: true, // Uses OpenSearch for relevance
		}),
		queryParamName: 'item_search',
		queryMapping: {
			objectTable: 'item',
			fieldName: 'description',
			joinType: 'direct',
		},
	},

	itemCategories: {
		type: 'multi-select',
		label: 'Categories',
		placeholder: 'Search product categories...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'category' as keyof unknown,
			primary: 'category' as keyof unknown,
			count: 'item_count' as keyof unknown,
		},
		apiEndpoint: '/items/categories',
		queryParamName: 'itemCategories',
		multiple: true,
		// PD-14109: count reflects the currently filtered Items list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'item',
			fieldName: 'category',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [
			'item',
			'item-warehouse-embedded',
			'price_contract_item',
			'clientItemHistory',
		],
	},

	itemCompany: {
		...createEntityLookupFilter({
			label: 'Company',
			placeholder: 'Search companies...',
			apiEndpoint: '/users/companies',
			objectTable: 'item',
			fieldName: 'fk_item__id',
			listKeys: ['item', 'price_contract_item', 'clientItemHistory'],
			displayFields: {
				id: 'id',
				primary: 'label',
			},
			colorScheme: 'purple',
			joinType: 'exists',
			joinTable: 'item_company',
			joinConditions: {
				sourceField: '_id',
				targetField: 'fk_item__id',
				filterField: 'fk_company__id',
			},
		}),
		queryParamName: 'itemCompany',
		apiParams: { countFor: 'items' },
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	itemManufacturers: {
		type: 'multi-select',
		label: 'Manufacturers',
		placeholder: 'Search manufacturers...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'manufacturer_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
			count: 'item_count' as keyof unknown,
		},
		apiEndpoint: '/items/manufacturers/search',
		queryParamName: 'itemManufacturers',
		multiple: true,
		// PD-14109: count reflects the currently filtered Items list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'item',
			fieldName: 'manufacturer',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [
			'item',
			'item-warehouse-embedded',
			'price_contract_item',
			'clientItemHistory',
		],
	},

	itemStatus: {
		...createRadioFilter({
			label: 'Status',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Active', label: 'Active' },
				{ id: 'Inactive', label: 'Inactive' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'item',
			fieldName: 'isActive',
			listKeys: ['item', 'price_contract_item', 'clientItemHistory'],
			colorScheme: 'green',
		}),
		queryParamName: 'itemStatus',
	},

	// Tri-state radio on the item.isShortDate boolean column (sourced from the
	// tenant's ERP / item master). Distinct from the 90-day expirationDate
	// computation that the legacy `item_filters` multi-select's `short_date`
	// option uses — that variant looks at item.expirationDate, this one trusts
	// the explicit flag set upstream.
	itemShortDate: {
		...createRadioFilter({
			label: 'Short Date',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Short Date', label: 'Short Date' },
				{ id: 'Not Short Date', label: 'Not Short Date' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'item',
			fieldName: 'isShortDate',
			listKeys: ['item', 'clientItemHistory'],
			colorScheme: 'orange',
		}),
		queryParamName: 'itemShortDate',
	},

	// Product-variant model (docs/claude/product-variants/README.md). Only the
	// admin items list sends includeProductLevel, so this is the one surface
	// where 'Product' rows are visible and worth filtering on. A flat-catalog
	// tenant has only 'standalone' rows, so leaving the filter at 'All' is
	// indistinguishable from today.
	itemLevel: {
		...createRadioFilter({
			label: 'Level',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'standalone', label: 'Standalone' },
				{ id: 'product', label: 'Product' },
				{ id: 'variant', label: 'Variant' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'item',
			fieldName: 'itemLevel',
			listKeys: ['item'],
			colorScheme: 'purple',
		}),
		queryParamName: 'itemLevel',
	},

	itemGroups: {
		type: 'multi-select',
		label: 'Groups',
		placeholder: 'Search groups...',
		colorScheme: 'gray',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			count: 'itemCount' as keyof unknown,
		},
		apiEndpoint: '/groups',
		queryParamName: 'itemGroups',
		multiple: true,
		// PD-14109: count reflects the currently filtered Items list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'item',
			joinType: 'exists',
			joinTable: 'item_group',
			joinConditions: {
				sourceField: 'id',
				targetField: 'fk_item__id',
				filterField: 'fk_group__id',
			},
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['item', 'price_contract_item', 'clientItemHistory'],
	},

	// Internal filter used by the "Add Items to Group" modal to exclude
	// items that are already members of a given group
	excludeItemGroups: {
		type: 'multi-select',
		label: 'Exclude Groups',
		placeholder: 'Exclude groups...',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		queryParamName: 'excludeItemGroups',
		multiple: true,
		queryMapping: {
			objectTable: 'item',
			joinType: 'not_exists',
			joinTable: 'item_group',
			joinConditions: {
				// Must be the real PK column `_id` — the not_exists join builder
				// emits this field name verbatim (item._id), so `id` produced the
				// invalid `item.id`. Mirrors the working client excludeGroups filter.
				sourceField: '_id',
				targetField: 'fk_item__id',
				filterField: 'fk_group__id',
			},
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['item'],
	},

	itemClassifications: {
		...createRadioFilter({
			label: 'Item Classifications',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Star Product', label: 'Star Product' },
				{ id: 'High Performer', label: 'High Performer' },
				{ id: 'Steady Performer', label: 'Steady Performer' },
				{ id: 'Growth Opportunity', label: 'Growth Opportunity' },
				{ id: 'Needs Attention', label: 'Needs Attention' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'item',
			fieldName: 'classification',
			listKeys: ['item'],
			colorScheme: 'purple',
		}),
		queryParamName: 'itemClassifications',
		queryMapping: {
			objectTable: 'item',
			fieldName: 'classification',
			joinType: 'custom',
		},
	},

	itemClientTrend: {
		...createRadioFilter({
			label: 'Trend',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'increasing', label: 'Increasing' },
				{ id: 'decreasing', label: 'Decreasing' },
				{ id: 'stable', label: 'Stable' },
			],
			objectTable: 'item',
			fieldName: 'trend',
			listKeys: ['item'],
		}),
		queryParamName: 'itemClientTrend',
	},

	category: {
		type: 'multi-select',
		label: 'Category',
		placeholder: 'Select categories...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/clients/shop/categories',
		queryParamName: 'category',
		multiple: true,
		queryMapping: {
			objectTable: 'item',
			fieldName: 'category',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['item', 'client_shop', 'quote_shop'],
	},

	manufacturer: {
		type: 'multi-select',
		label: 'Manufacturer',
		placeholder: 'Select manufacturers...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/clients/shop/manufacturers',
		queryParamName: 'manufacturer',
		multiple: true,
		queryMapping: {
			objectTable: 'item',
			fieldName: 'manufacturer',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['item', 'client_shop', 'quote_shop'],
	},

	subcategory: {
		type: 'multi-select',
		label: 'Subcategory',
		placeholder: 'Select subcategories...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/clients/shop/subcategories',
		queryParamName: 'subcategory',
		multiple: true,
		queryMapping: {
			objectTable: 'item',
			fieldName: 'subcategory',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['item', 'client_shop', 'quote_shop'],
	},

	stock_status: {
		...createRadioFilter({
			label: 'Stock Status',
			options: [
				{ id: 'all', label: 'All' },
				{ id: 'in_stock', label: 'In Stock' },
				{ id: 'out_of_stock', label: 'Out of Stock' },
				{ id: 'low_stock', label: 'Low Stock' },
				{ id: 'on_order', label: 'On Order' },
			],
			defaultValue: { id: 'all', label: 'All' },
			objectTable: 'item',
			fieldName: 'quantityOnHand',
			listKeys: [
				'item',
				'client_shop',
				'quote_shop',
				'price_contract_item',
				'clientItemHistory',
			],
			colorScheme: 'green',
		}),
		queryParamName: 'stock_status',
		queryMapping: {
			objectTable: 'item',
			fieldName: 'quantityOnHand',
			joinType: 'custom',
		},
	},

	in_cart: {
		...createRadioFilter({
			label: 'Cart Status',
			options: [
				{ id: 'all', label: 'All Items' },
				{ id: 'in_cart', label: 'In Cart' },
				{ id: 'not_in_cart', label: 'Not In Cart' },
			],
			defaultValue: { id: 'all', label: 'All Items' },
			objectTable: 'item',
			fieldName: 'inCart',
			listKeys: ['item', 'client_shop', 'quote_shop'],
			colorScheme: 'yellow',
		}),
		queryParamName: 'in_cart',
		queryMapping: {
			objectTable: 'item',
			joinType: 'custom',
		},
	},

	item_filters: {
		type: 'multi-select',
		label: 'Item Filters',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'item_filters',
		multiple: true,
		options: [
			{ id: 'in_cart', label: 'In Cart' },
			{ id: 'in_stock', label: 'In Stock' },
			{ id: 'on_order', label: 'On Order' },
			{ id: 'short_date', label: 'Short Date' },
			{ id: 'favorite', label: 'Favorite' },
			{ id: 'reorder', label: 'Reorder' },
			{ id: 'at_risk', label: 'At Risk' },
			{ id: 'previously_purchased', label: 'Previously Purchased' },
			{ id: 'recommended', label: 'Recommended' },
			{ id: 'popular', label: 'Popular' },
			{ id: 'recently_restocked', label: 'Recently Restocked' },
			{ id: 'newly_stocked', label: 'Newly Stocked' },
			{ id: 'on_promo', label: 'On Promo' },
		],
		queryMapping: {
			objectTable: 'item',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['item', 'client_shop', 'quote_shop'],
	},

	favorite_status: {
		...createRadioFilter({
			label: 'Favorites',
			options: [
				{ id: 'all', label: 'All Items' },
				{ id: 'favorites', label: 'Favorites Only' },
				{ id: 'non_favorites', label: 'Non-Favorites' },
			],
			defaultValue: { id: 'all', label: 'All Items' },
			objectTable: 'client_item',
			fieldName: 'favorite',
			listKeys: ['item', 'client_shop', 'quote_shop'],
			colorScheme: 'yellow',
		}),
		queryParamName: 'favorite_status',
		queryMapping: {
			objectTable: 'client_item',
			fieldName: 'favorite',
			joinType: 'custom',
		},
	},

	items: {
		type: 'multi-select',
		label: 'Items',
		placeholder: 'Type to search items...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'item_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
			secondary: 'manufacturer' as keyof unknown,
		},
		apiEndpoint: '/items/search',
		queryParamName: 'items',
		multiple: true,
		queryMapping: {
			objectTable: 'item',
			fieldName: '_id',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['item'],
	},

	productCategories: {
		type: 'multi-select',
		label: 'Product Categories',
		placeholder: 'Search product categories...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'category' as keyof unknown,
			primary: 'category' as keyof unknown,
			count: 'item_count' as keyof unknown,
		},
		apiEndpoint: '/items/categories',
		queryParamName: 'productCategories',
		multiple: true,
		// PD-14109: count reflects the currently filtered Items list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'item',
			fieldName: 'category',
			joinType: 'direct',
		},
		listKeys: ['item'],
	},

	// Programmatic filter: constrains item list to items in a specific price contract
	// Not user-facing — injected by the price contract controller
	itemPriceContractId: {
		type: 'text',
		label: 'Price Contract',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'priceContractId',
		multiple: false,
		queryMapping: {
			objectTable: 'item',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['item', 'price_contract_item'],
	},

	// Programmatic filter: excludes items already on a specific price contract
	// Not user-facing — injected by the price contract "add items" picker
	notInItemPriceContractId: {
		type: 'text',
		label: 'Not in Price Contract',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'notInPriceContractId',
		multiple: false,
		queryMapping: {
			objectTable: 'item',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['item'],
	},

	itemLotKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder:
				'Search by lot number, lot external ID, item description, or item external ID...',
			objectTable: 'item_lot',
			listKeys: [
				'itemLot',
				'itemLot-item-embedded',
				'itemLot-warehouse-embedded',
			],
			fulltextFields: [
				'item_lot.lotNumber',
				'item_lot.externalId',
				'item.description',
				'item.externalId',
			],
		}),
		queryParamName: 'itemLotKeywords',
		queryMapping: {
			objectTable: 'item_lot',
			joinType: 'join',
			joinTable: 'item',
			fulltextFields: [
				'item_lot.lotNumber',
				'item_lot.externalId',
				'item.description',
				'item.externalId',
			],
		},
	},

	itemLotExpirationDate: {
		...createDateRangeFilter({
			label: 'Expiration Date',
			objectTable: 'item_lot',
			dateField: 'expirationDate',
			listKeys: [
				'itemLot',
				'itemLot-item-embedded',
				'itemLot-warehouse-embedded',
			],
		}),
		queryParamName: 'itemLotExpirationDate',
		colorScheme: 'orange',
		allowClear: true,
	},

	// Date range on item.lastRestockedDate. Used both as a free-form filter
	// on the main item list and as the back-end of the "Recently Restocked"
	// tab (which seeds it with the last 30 days, matching the client-shop
	// "Recently Restocked" filter).
	itemRestockedDateRange: {
		...createDateRangeFilter({
			label: 'Restocked Date',
			objectTable: 'item',
			dateField: 'lastRestockedDate',
			listKeys: ['item', 'clientItemHistory'],
		}),
		queryParamName: 'itemRestockedDateRange',
		allowClear: true,
	},

	// PD-11124 — date range on item.priceModifiedDatetime, the timestamp
	// ItemPriceTrackingSubscriber stamps whenever it rolls the old list price
	// into `previousPrice`. Used both as a free-form filter on the main item
	// list and as the back-end of the "Price Changes" tab (which seeds it with
	// the last 7 days).
	itemPriceChangedDateRange: {
		...createDateRangeFilter({
			label: 'Price Changed Date',
			objectTable: 'item',
			dateField: 'priceModifiedDatetime',
			listKeys: ['item'],
		}),
		queryParamName: 'itemPriceChangedDateRange',
		allowClear: true,
	},

	// PD-11124 — narrows the "Price Changes" tab to increases or decreases.
	// Any difference between `price` and `previousPrice` counts, no matter how
	// small: the tenant that asked for this wants every change surfaced, so
	// there is deliberately no epsilon/threshold.
	itemPriceChangeDirection: {
		...createRadioFilter({
			label: 'Price Direction',
			options: [
				// id must NOT be the literal 'all' — FilterService drops
				// radio values with that id (platform "All = no filter"
				// convention). Here "show everything" is also the default, so
				// dropping it would be harmless today, but the id would become
				// a silent trap the moment the default changes. Mirrors the
				// `priceContractType` filter in order.ts.
				{ id: 'any', label: 'All' },
				{ id: 'increased', label: 'Increased' },
				{ id: 'decreased', label: 'Decreased' },
			],
			defaultValue: { id: 'any', label: 'All' },
			objectTable: 'item',
			// Nominal only — the predicate compares `price` against
			// `previousPrice`, which the custom branch in typeormFilterBuilder
			// builds; no single column matches this filter directly.
			fieldName: 'price',
			listKeys: ['item'],
			colorScheme: 'purple',
		}),
		queryParamName: 'itemPriceChangeDirection',
		queryMapping: {
			objectTable: 'item',
			fieldName: 'price',
			joinType: 'custom',
		},
	},

	// Date range filtering "when this item became inventory." Targets
	// COALESCE(externalCreatedDate, DATE(_created_date)) so the result
	// matches the client-shop "Newly Stocked" tab exactly. Without the
	// COALESCE, the filter would only catch items whose DB row was inserted
	// recently — meaningless on tenants that bulk-imported their catalog
	// (e.g. Pharmsource), where every existing item has the same import
	// date and recent ERP additions only differ via externalCreatedDate.
	itemCreatedDateRange: {
		...createDateRangeFilter({
			label: 'Created Date',
			objectTable: 'item',
			dateField: '_created_date',
			dateExpression:
				'COALESCE(item.externalCreatedDate, DATE(item._created_date))',
			listKeys: ['item', 'clientItemHistory'],
		}),
		queryParamName: 'itemCreatedDateRange',
		allowClear: true,
	},

	itemLotProductionDate: {
		...createDateRangeFilter({
			label: 'Production Date',
			objectTable: 'item_lot',
			dateField: 'productionDate',
			listKeys: [
				'itemLot',
				'itemLot-item-embedded',
				'itemLot-warehouse-embedded',
			],
		}),
		queryParamName: 'itemLotProductionDate',
		allowClear: true,
	},

	// PD-14018: trend window for the client Items Insights tab. Picks how far
	// back the increasing/decreasing/stable comparison looks (last N days vs
	// the N days before that). The backend whitelists 30/60/90 and falls back
	// to 90, so a missing/odd value is harmless.
	itemHistoryTrendWindow: {
		...createRadioFilter({
			label: 'Trend Window',
			options: [
				{ id: '30', label: 'Last 30 days' },
				{ id: '60', label: 'Last 60 days' },
				{ id: '90', label: 'Last 90 days' },
			],
			defaultValue: { id: '90', label: 'Last 90 days' },
			objectTable: 'invoice',
			fieldName: 'date',
			listKeys: ['clientItemHistory'],
			colorScheme: 'blue',
		}),
		queryParamName: 'itemHistoryTrendWindow',
	},

	// "Hide depleted" stock filter for the item Lots tab (PD-13738). A radio
	// rather than a checkbox because the filter sidebar has no checkbox
	// control; selecting "In Stock" hides depleted lots. 'All' is the
	// parse-time no-op sentinel, so the backend only sees the other two ids.
	// Scoped to the item-embedded list — the warehouse/location lot endpoints
	// don't read this param.
	itemLotStock: {
		...createRadioFilter({
			label: 'Stock',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'In Stock', label: 'In Stock' },
				{ id: 'Depleted', label: 'Depleted' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'item_lot',
			fieldName: 'quantityRemaining',
			listKeys: ['itemLot-item-embedded'],
			colorScheme: 'green',
		}),
		queryParamName: 'itemLotStock',
	},

	itemLotWarehouse: {
		...createEntityLookupFilter({
			label: 'Warehouse',
			placeholder: 'Select warehouse...',
			apiEndpoint: '/warehouses',
			objectTable: 'item_lot',
			fieldName: 'fk_warehouse__id',
			listKeys: ['itemLot'],
			displayFields: {
				id: '_id',
				primary: 'name',
				secondary: 'address',
			},
			colorScheme: 'purple',
		}),
		queryParamName: 'itemLotWarehouse',
		allowClear: true,
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},
};
